// Side-effect import: extends Day.js with the utc, timezone and
// customParseFormat plugins before any call site uses them. (`advancedFormat`
// went with the last `Do` token — `formatDay` spells the day out in full.)
import "utils/dayjs";

import CssBaseline from "@mui/material/CssBaseline";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license";
import * as Sentry from "@sentry/react";
import App from "App";
import ErrorFallback from "components/ErrorFallback";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import firebaseConfig from "firebaseConfig";
import React from "react";
import { createRoot } from "react-dom/client";
import theme from "theme";

try {
  initializeApp(firebaseConfig);
  getAnalytics();
  // initializeAppCheck(app, {
  //   provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_V3),
  //
  //   // Optional argument. If true, the SDK automatically refreshes App Check
  //   // tokens as needed.
  //   isTokenAutoRefreshEnabled: true
  // });
} catch (err) {
  Sentry.captureException(err);
}

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

// Build a regex that matches a URL only when the given hostname is the host
// (not a substring that happens to appear inside a query string — for example
// Beamer's `&domain=localhost%3A3000` would otherwise match a bare "localhost"
// entry in tracePropagationTargets and attach an unwelcome sentry-trace header).
// Input is normalised so both `api.example.com` and `https://api.example.com/v2`
// produce the same regex; otherwise a full-URL value would double-up the scheme
// and silently fail to match anything.
const hostRegex = (input) => {
  const hostname = input
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
  const escaped = hostname.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^https?:\\/\\/${escaped}(?::\\d+)?(?:\\/|$)`);
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_RELEASE,
  environment: import.meta.env.VITE_ENV,
  tracePropagationTargets: [
    hostRegex("localhost"),
    import.meta.env.VITE_ALLOWED_TRACING_URL &&
      hostRegex(import.meta.env.VITE_ALLOWED_TRACING_URL),
  ].filter(Boolean),
  integrations: [
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest: (url) => !url.match(/getbeamer\.com/),
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.3,
});

const container = document.querySelector("#root");
const root = createRoot(container);

root.render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          <App />
        </Sentry.ErrorBoundary>
      </LocalizationProvider>
    </ThemeProvider>
  </StyledEngineProvider>,
);
